<!-- https://www.smashingmagazine.com/2014/05/responsive-images-done-right-guide-picture-srcset/ -->
<template>
  <AdaptivePicture
    v-if="picture"
    :picture="picture"
    :width="width"
    :height="height"
    :srcset="srcSetPicture"
    :sizes="sizesPicture"
    :loading="loading"
    :decoding="decoding"
  />
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type { TImageOriginFull, TImageSize } from '~/types/common';
  import type { ImageSizesByDevice } from '@devhacker/shared/utils/image/types';
  import {
    getSrcSetByDPR,
    getSizesPicture,
    getImagesBySizes,
    getOriginImageFormat,
  } from '@devhacker/shared/utils/image';
  import AdaptivePicture from './AdaptivePicture.vue';

  export default defineNuxtComponent({
    name: 'AdaptivePictureAccentCard',

    components: {
      AdaptivePicture,
    },

    props: {
      picture: {
        type: Object as PropType<TImageOriginFull | null>,
        required: true,
      },

      size: {
        type: String as PropType<'big' | 'middle' | 'small'>,
        default: 'small',
      },

      loading: {
        type: String as PropType<'lazy' | 'eager' | 'auto'>,
        default: undefined,
      },

      decoding: {
        type: String as PropType<'sync' | 'async' | 'auto'>,
        default: undefined,
      },
    },

    data: () => ({
      // размеры из нарезанных в 1x,
      // которые буду использоваться
      // https://www.figma.com/file/JGnLzMLGla9gSsvnW00NUY/Burning-Design-System-%F0%9F%94%A5?node-id=3138%3A136697&t=DS2oY88GzSsNdmHN-1
      articleCoverSizes: [390, 748] as number[],
      sourcesByType: {
        big: {
          mobile: 'calc(100vw - 16px * 2)',
          tablet: 'calc(100vw - 24px * 2 * 2)',
          desktop: '748px',
        },

        middle: {
          mobile: 'calc(100vw - 16px * 2)',
          tablet: 'calc(100vw - 24px * 2 * 2)',
          desktop: '390px',
        },

        small: {
          mobile: 'calc(100vw - 16px * 2)',
          tablet: 'calc((100vw - 24px * 2 * 2) / 2)',
          desktop: '390px',
        },
      } as Record<string, any>,
    }),

    computed: {
      sizesByDevice(): ImageSizesByDevice {
        const { sourcesByType, size } = this;

        return sourcesByType[size];
      },

      sizes(): TImageSize[] {
        return this.picture?.sizes || [];
      },

      imagesBySizes(): TImageSize[] {
        return getImagesBySizes(this.articleCoverSizes, this.sizes);
      },

      imagesBySizesFirst(): TImageSize {
        const [firstImage] = this.imagesBySizes;

        return firstImage;
      },

      imageOrigin(): TImageSize {
        return getOriginImageFormat(this.picture?.origin);
      },

      width(): number {
        return this.imagesBySizesFirst.width || this.imageOrigin.width;
      },

      height(): number {
        return this.imagesBySizesFirst.height || this.imageOrigin.height;
      },

      srcSetPicture(): string {
        if (this.size === 'big') {
          return getSrcSetByDPR([748], this.sizes);
        }
        return getSrcSetByDPR(this.articleCoverSizes, this.sizes);
      },

      sizesPicture(): string {
        return getSizesPicture(this.sizesByDevice);
      },
    },

    methods: {},
  });
</script>

<style lang="scss" scoped>
  :deep() {
    // important из-за scss/article.scss
    // пересмотреть его: зачем там указывать сили для picture__img
    // если он есть в компоненте картиник и можно задать в обертке
    .picture__img {
      height: 100% !important;
      object-fit: cover !important;
    }
  }
</style>
